import { useState } from 'react'
import { api } from '../lib/api'
import { setCachedUser } from '../lib/auth'
import type { User } from '../lib/types'

/**
 * One-time "what should we call you?" (v0.18.0, D-109).
 * The funnel showed 0 of 8 members had a display name: everyone appeared as
 * "hrsd0001" rather than a person, AND Find-your-place self-matching keys off
 * user.name, so it silently found nothing for all of them. Members who joined
 * before the Seed Ceremony saved the name (v0.13.0) were never asked again.
 * Dismissible, never nags twice in a session, disappears the moment a name exists.
 */
export default function NamePrompt({ user, onUpdate }: { user: User; onUpdate: (u: User) => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('fa_name_prompt_dismissed') === '1')

  if (hidden || (user.name && user.name.trim())) return null

  const save = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      const updated = await api.updateProfile(name.trim())
      setCachedUser(updated)
      onUpdate(updated)
    } catch { /* non-fatal — they can set it in Profile */ }
    finally { setBusy(false) }
  }

  return (
    <div className="card-glass p-5 mb-6 border-gold-500/25 fade-up">
      <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">What should your family call you?</h2>
      <p className="text-xs text-slate-500 mb-4">
        Right now you appear as <span className="text-slate-400">{user.email.split('@')[0]}</span>. Your name also lets us
        find <b>you</b> in the family tree — it's how "this is me" works.
      </p>
      <div className="flex flex-wrap gap-2">
        <input
          value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save() }}
          placeholder="e.g. Hawra Alshamary" autoFocus
          className="input-dark !py-2 text-sm flex-1 min-w-[200px]"
        />
        <button onClick={save} disabled={busy || !name.trim()} className="btn-gold !py-2 text-sm">
          {busy ? 'Saving…' : 'That’s me'}
        </button>
        <button
          onClick={() => { sessionStorage.setItem('fa_name_prompt_dismissed', '1'); setHidden(true) }}
          className="btn-ghost !py-2 text-sm">Later</button>
      </div>
    </div>
  )
}
